/**
 * hooks/deposits/useDepositsList.ts
 *
 * List hook for consumer's manual deposits history.
 * Registered in listHooks as "manual_deposits".
 */

import {
  ArrowDownCircleOutline,
  CheckmarkCircleOutline,
  CloseCircleOutline,
  TimeOutline,
  HourglassOutline,
  OpenOutline,
} from "@raresail/react-ionicons";
import {
  IconBox,
  ListItemWrapper,
  StatusTag,
} from "../../components/elements/Elements";
import { API_MANUAL_DEPOSITS } from "../../constants/apis";
import { MANUAL_DEPOSITS, NEW_MANUAL_DEPOSIT, SELECT } from "../../constants/constants";
import { searchFilterField } from "../../constants/options";
import {
  addSelectOption,
  currencyFormat,
  formatDate,
  formatDateTime,
  stringify,
} from "../../utils/helpers";
import { openBottomSheet, openModalForm } from "../../utils/modalUtils";

export const useDepositsList = () => {
  const filters = [
    searchFilterField,
    {
      field: "status",
      type: SELECT,
      options: ["pending", "credited", "rejected"].map((v) =>
        addSelectOption(v)
      ),
    },
  ];

  const viewData = (data: any) => {
    const {
      amount,
      currency,
      status,
      description,
      charges_deducted,
      net_amount,
      proof_of_payment,
      rejection_reason,
      created_at,
      approved_at,
    } = data || {};

    return {
      amount: currencyFormat(amount, currency),
      status: <StatusTag status={status} />,
      description: description || "—",
      ...(status === "credited"
        ? {
            charges: currencyFormat(charges_deducted),
            net_credited: currencyFormat(net_amount),
          }
        : {}),
      ...(proof_of_payment
        ? {
            proof: (
              <a
                href={proof_of_payment}
                target="_blank"
                rel="noopener noreferrer"
                className="main semiBold font12"
              >
                View POP ↗
              </a>
            ),
          }
        : {}),
      ...(rejection_reason ? { reason: rejection_reason } : {}),
      submitted: formatDateTime(created_at),
      ...(approved_at ? { processed: formatDateTime(approved_at) } : {}),
    };
  };

  const ListItem = ({ data }: { data: any }) => {
    const { amount, currency, status, description, created_at } = data || {};

    return (
      <ListItemWrapper
        className="flexNullCenter gap15"
        onClick={() =>
          openBottomSheet({
            component: "details",
            title: "Deposit Details",
            source: MANUAL_DEPOSITS,
            data,
          })
        }
      >
        <IconBox icon={ArrowDownCircleOutline} size={20} />

        {/* MID */}
        <div className="flexGrow">
          <div className="font13 semiBold textDark">
            {currencyFormat(amount, currency)}
          </div>
          <div className="font11 grey ellipsis">
            {description || "Manual deposit"}
          </div>
          <div className="font10 grey flexNullCenter gap5 mt3">
            <TimeOutline fontSize={11} />
            {formatDate(created_at)}
          </div>
        </div>

        {/* END */}
        <div className="textEnd">
          <StatusTag status={status} />
        </div>
      </ListItemWrapper>
    );
  };

  return {
    ListItem,
    title: "My Deposits",
    subtitle: "Manual deposit history",
    api: API_MANUAL_DEPOSITS,
    filters,
    viewData,
    action: {
      onClick: () => openModalForm({ source: NEW_MANUAL_DEPOSIT }),
    },
  };
};